import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import FormikTextField from '../../../shared/components/formik/FormikTextField'
import useHetznerProjectsResources from '../../../shared/resources/api/v1/cloud/useHetznerProjectsResources'
import { useTeam } from '../../../contexts/TeamContext'
import { useHetznerProjects } from '../../../contexts/HetznerProjectsContext'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'

function HetznerProjectCreatePage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { create } = useHetznerProjectsResources(teamSlug)
  const { reload } = useHetznerProjects()

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    apiToken: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      apiToken: '',
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const projectData = {
        project: {
          name: values.name,
          apiToken: values.apiToken,
        },
      }

      create(projectData)
        .then(() => reload())
        .then(() => {
          navigate(`/teams/${team.slug}/cloud/hetzner`)
        })
        .catch((err) => {
          setStatus(err.message || 'Error creating project')
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  })

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team }} />
        <Typography variant="h4" component="h1" gutterBottom>
          <FormattedMessage id="hetzner.project.create.title" />
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          <FormikTextField
            formik={formik}
            name="name"
            label={<FormattedMessage id="hetzner.project.create.nameLabel" />}
            autoFocus
          />

          <Box sx={{ mt: 2 }}>
            <FormikTextField
              formik={formik}
              name="apiToken"
              label={<FormattedMessage id="hetzner.project.create.apiTokenLabel" />}
              type="password"
              helperText={<FormattedMessage id="hetzner.project.create.apiTokenHint" />}
            />
          </Box>

          {formik.status && (
            <Typography color="error" sx={{ mt: 2 }}>
              {formik.status}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting}
            sx={{ mt: 3 }}
          >
            <FormattedMessage id="hetzner.project.create.submitButton" />
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default HetznerProjectCreatePage
